import type { IClient, IClientApiItem, IClientsSort } from '../../interfaces/clients';
import type { IProspect } from '../../interfaces/prospects';
import { ClientsStatus, type ClientsSortKey } from '../../types/clients';
import type { SortDirection } from '../../types/globals';

export const filterClientsByStatus = (clients: IClient[], status: ClientsStatus): IClient[] =>
  clients.filter((client) => client.status === status);

export const isClientsStatus = (value: unknown): value is ClientsStatus =>
  value === ClientsStatus.Active || value === ClientsStatus.AwaitingStart;

export const resolveClientProfileStatus = (param: string | null, stateStatus?: ClientsStatus): ClientsStatus | null => {
  if (isClientsStatus(param)) return param;
  if (isClientsStatus(stateStatus)) return stateStatus;
  return null;
};

const buildLocation = (...parts: (string | null | undefined)[]): string | null => {
  const defined = parts.filter((part): part is string => Boolean(part));
  return defined.length ? defined.join(', ') : null;
};

export const mapProspectToClient = (prospect: IProspect): IClient => ({
  id: prospect.id,
  companyName: prospect.companyName,
  workforceSize: prospect.workforceSize,
  companySize: null,
  industry: prospect.industry ?? null,
  activity: null,
  status: ClientsStatus.AwaitingStart,
  location: buildLocation(prospect.city, prospect.state),
  enrollmentWindow: prospect.enrollmentWindow ?? null,
  activationDate: null,
  isNew: false,
  hasActivationError: Boolean(prospect.error),
  documentValue: prospect.documentValue ?? null
});

export const mapProspectsToClients = (prospects: IProspect[]): IClient[] => prospects.map(mapProspectToClient);

export const mapClientApiToClient = (item: IClientApiItem): IClient => ({
  id: item.id,
  companyName: item.companyName ?? item.businessName ?? '',
  workforceSize: item.workforceSize ?? null,
  companySize: item.companySize ?? null,
  industry: item.industry ?? null,
  activity: item.activity ?? null,
  status: ClientsStatus.Active,
  location: buildLocation(item.city, item.state),
  enrollmentWindow: null,
  activationDate: item.activationDate ?? null,
  isNew: false,
  hasActivationError: false,
  documentValue: item.documentValue ?? null
});

export const mapClientApiToClients = (items: IClientApiItem[]): IClient[] => items.map(mapClientApiToClient);

export const filterClientsByQuery = (clients: IClient[], query: string): IClient[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return clients;
  return clients.filter((client) => client.companyName.toLowerCase().includes(needle));
};

const recencyValue = (client: IClient): number => {
  const time = client.activationDate ? Date.parse(client.activationDate) : Number.NaN;
  return Number.isNaN(time) ? 0 : time;
};

const compareByKey = (a: IClient, b: IClient, key: ClientsSortKey): number => {
  switch (key) {
    case 'companyName':
      return a.companyName.localeCompare(b.companyName);
    case 'workforceSize':
      return (a.workforceSize ?? 0) - (b.workforceSize ?? 0);
    case 'enrollmentWindow':
      return (a.enrollmentWindow ?? '').localeCompare(b.enrollmentWindow ?? '');
    default:
      return recencyValue(a) - recencyValue(b);
  }
};

export const sortClients = (clients: IClient[], { key, direction }: IClientsSort): IClient[] =>
  [...clients].sort((a, b) => {
    const comparison = compareByKey(a, b, key);
    return direction === 'asc' ? comparison : -comparison;
  });

export const getTotalPages = (total: number, pageSize: number): number => Math.max(1, Math.ceil(total / pageSize));

export const getSafePage = (page: number, totalPages: number): number => Math.min(Math.max(page, 1), totalPages);

export const paginateClients = (clients: IClient[], page: number, pageSize: number): IClient[] => {
  const start = (page - 1) * pageSize;
  return clients.slice(start, start + pageSize);
};

export const toggleSort = (current: IClientsSort, key: ClientsSortKey): IClientsSort => {
  if (current.key !== key) return { key, direction: 'asc' };
  const direction: SortDirection = current.direction === 'asc' ? 'desc' : 'asc';
  return { key, direction };
};
